/**
 * Column header row for table. Uses shared grid template.
 */

import type { CSSProperties, FC } from 'react';
import { cn } from '@/lib/utils';
import {
  draftsHeaderGridStyle,
  draftsHeaderGridWithCountryStyle,
  draftsHeaderGridWithDragAndCountryStyle,
  draftsHeaderGridWithDragStyle,
} from '../../shared/grid';
import type { SentSection, SortKey } from '../../shared/types';

function SortableHeader({
  label,
  align = 'left',
  className,
  isActive,
  sortDir,
  onClick,
}: {
  label: string;
  align?: 'left' | 'right';
  className?: string;
  isActive: boolean;
  sortDir?: 'asc' | 'desc';
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Sort by ${label} ${isActive ? (sortDir === 'asc' ? 'ascending' : 'descending') : ''}`}
      className={cn(
        'flex w-full min-w-0 items-center rounded px-2 py-1 text-xs font-medium transition-colors hover:bg-muted/50 hover:text-foreground',
        align === 'left' && 'justify-start text-left',
        align === 'right' && 'justify-end text-right',
        isActive && 'bg-primary/10 text-primary font-semibold',
        !isActive && 'text-muted-foreground',
        className,
      )}
    >
      {label}
    </button>
  );
}

interface ColumnHeadersRowProps {
  activeTab: 'drafts' | 'sent';
  sentSection?: SentSection;
  sortBy?: SortKey | null;
  sortDir?: 'asc' | 'desc';
  onSort?: (key: SortKey) => void;
  showDragHandle?: boolean;
  showCountryColumn?: boolean;
  headerGridStyle?: CSSProperties;
}

export const ColumnHeadersRow: FC<ColumnHeadersRowProps> = ({
  activeTab,
  sentSection = 'sent',
  sortBy,
  sortDir,
  onSort,
  showDragHandle,
  showCountryColumn = false,
  headerGridStyle,
}) => {
  const defaultGridStyle =
    showDragHandle
      ? showCountryColumn
        ? draftsHeaderGridWithDragAndCountryStyle
        : draftsHeaderGridWithDragStyle
      : showCountryColumn
        ? draftsHeaderGridWithCountryStyle
        : draftsHeaderGridStyle;
  const renderColumn = (sortKey: SortKey, label: string, align: 'left' | 'right' = 'left', className?: string) => {
    if (!onSort) {
      return (
        <div
          className={cn(
            'flex w-full min-w-0 items-center text-xs font-medium text-muted-foreground',
            align === 'left' && 'justify-start text-left',
            align === 'right' && 'justify-end text-right',
            className,
          )}
        >
          {label}
        </div>
      );
    }
    return (
      <SortableHeader
        label={label}
        align={align}
        className={className}
        isActive={sortBy === sortKey}
        sortDir={sortDir}
        onClick={() => onSort(sortKey)}
      />
    );
  };

  return (
    <div
      className="sticky top-0 z-10 grid w-full items-stretch gap-x-3 border-b border-border/30 bg-background/95 px-4 py-2 text-xs font-medium text-muted-foreground backdrop-blur-sm"
      style={headerGridStyle ?? defaultGridStyle}
    >
      {showDragHandle && <div aria-hidden className="min-w-0" />}
      {showCountryColumn && renderColumn('country', 'Country', 'left', 'min-w-0')}
      {renderColumn('gateway', 'Gateway', 'left', 'min-w-0')}
      {renderColumn('creative', 'Creative', 'left', 'min-w-0')}
      {renderColumn('leads', 'Leads', 'left', 'min-w-0')}
      {renderColumn('campaign', 'Campaign', 'left', 'min-w-0')}
      {renderColumn('cost', 'Cost', 'right', 'min-w-0')}
      {renderColumn('amount', 'Amount', 'right', 'min-w-0')}
      {renderColumn('totalCost', 'Total cost', 'right', 'min-w-0')}
      {renderColumn('tld', 'TLD', 'left', 'min-w-0')}
      {renderColumn('staticTag', 'Static Tag', 'left', 'min-w-0')}
      {renderColumn('scheduled', 'Planned send', 'left', 'min-w-0')}
      {renderColumn('localSend', 'Local send', 'left', 'min-w-0')}
      {activeTab === 'sent'
        ? renderColumn(
            'time',
            sentSection === 'scheduled' ? 'Scheduled' : sentSection === 'killed' ? 'Killed at' : 'Sent at',
            'left',
            'min-w-0',
          )
        : renderColumn('created', 'Created', 'left', 'min-w-0')}
      {renderColumn('status', 'Status', 'left', 'min-w-0')}
      <div className="flex min-w-0 items-center justify-end text-xs font-medium text-muted-foreground">
        Actions
      </div>
    </div>
  );
};
